import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { firebaseAuth } from '@/lib/firebase';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Card, CardContent } from '@/components/ui/card';
import { XCircle } from 'lucide-react';

// Canonical post-login destination — mirrors AuthPage's DEFAULT_REDIRECT.
const POST_LOGIN_REDIRECT = '/chat';

/**
 * ImpersonateCallbackPage — admin "view as user" landing page.
 *
 * An admin (on admin.jdsimplified.com) mints a short-lived Firebase custom token
 * carrying { impersonation: true, impersonated_by: <adminUid> } and opens
 *   https://<user-domain>/impersonate-callback#token=<customToken>
 * in a new tab. This page signs into that token so the admin sees the app exactly
 * as the user does. The ImpersonationBanner then marks the whole session.
 *
 * Security / robustness:
 * - The token is read from the URL fragment and IMMEDIATELY scrubbed with
 *   history.replaceState (before any async work) so it never lingers in the URL
 *   bar or browser history.
 * - The token is never logged.
 * - A tokenless or malformed visit is inert: it renders the same error state,
 *   never crashes, and never redirects (no loop).
 */
export default function ImpersonateCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'working' | 'error'>('working');
  // Guards against React 18 StrictMode's double effect-invoke (same fiber, so
  // the ref persists) attempting a second sign-in after the token is scrubbed.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // 0. Domain guard (defense-in-depth). Impersonation may ONLY complete on the
    //    internal dev surface — never a public prod origin (jdsimplified.com,
    //    studicata, etc.). dev shares the prod DB, so the real user data is visible
    //    on dev without exposing "view as user" on a public domain. Even if this
    //    route ships to `main`, it stays inert on prod. The admin host-picker is
    //    dev-only too (AdminUserDetailPage IMPERSONATE_HOSTS); this is the backstop.
    const IMPERSONATION_ALLOWED_HOSTS = new Set([
      'dev.jdsimplified.com',
      'localhost',
      '127.0.0.1',
    ]);
    if (!IMPERSONATION_ALLOWED_HOSTS.has(window.location.hostname)) {
      // Do not lift or leave the bearer token anywhere on a blocked host.
      try {
        delete (window as unknown as { __IMPERSONATION_TOKEN__?: string })
          .__IMPERSONATION_TOKEN__;
      } catch {
        (window as unknown as { __IMPERSONATION_TOKEN__?: string })
          .__IMPERSONATION_TOKEN__ = undefined;
      }
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );
      // Render the SAME generic "link not valid" state as a bad/expired token —
      // do NOT reveal that impersonation works on some other domain (no recon hint).
      setStatus('error');
      return;
    }

    // 1. Read the token. PRIMARY source: the pre-boot scrub
    //    (public/impersonation-scrub.js) captured it into a global and stripped
    //    the fragment BEFORE the app bundle + analytics loaded — so the bearer
    //    token was never exposed to Usermaven's autoPageview. Read + clear it.
    let token: string | null = null;
    const preboot = (window as unknown as { __IMPERSONATION_TOKEN__?: string })
      .__IMPERSONATION_TOKEN__;
    if (typeof preboot === 'string' && preboot) {
      token = preboot;
      try {
        delete (window as unknown as { __IMPERSONATION_TOKEN__?: string })
          .__IMPERSONATION_TOKEN__;
      } catch {
        (window as unknown as { __IMPERSONATION_TOKEN__?: string })
          .__IMPERSONATION_TOKEN__ = undefined;
      }
    }

    // FALLBACK: if the pre-boot script didn't run (e.g. served uncached, or a
    // dev/build variant without it), read the fragment directly. Then scrub it
    // synchronously — before any await — so it never lingers in URL/history.
    if (!token) {
      try {
        const hash = window.location.hash.startsWith('#')
          ? window.location.hash.slice(1)
          : window.location.hash;
        token = new URLSearchParams(hash).get('token');
      } catch {
        token = null;
      }
    }

    // Ensure the fragment is stripped regardless (no-op if the pre-boot script
    // already cleared it). Keeps path + any query intact.
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );

    // 2. Tokenless / garbage visit is inert — show the error state, do nothing else.
    if (!token) {
      setStatus('error');
      return;
    }

    const signInAsUser = async (customToken: string) => {
      try {
        // Drop any existing session first so we don't blend the admin's own
        // login with the impersonated user.
        if (firebaseAuth.currentUser) {
          await signOut(firebaseAuth);
        }
        await signInWithCustomToken(firebaseAuth, customToken);
        // Success — hand off to the canonical post-login route.
        navigate(POST_LOGIN_REDIRECT, { replace: true });
      } catch {
        // Never log the token. Any failure (expired/invalid/revoked) lands on the
        // same clear, terminal error state — no retry, no redirect loop.
        setStatus('error');
      }
    };

    void signInAsUser(token);
  }, [navigate]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center animated-gradient p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            {status === 'working' && (
              <>
                <LoadingSpinner size="lg" className="mx-auto" />
                <h2 className="text-xl font-semibold">Opening view-as session…</h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Signing you in to view this account. One moment.
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="w-16 h-16 text-red-500 mx-auto" />
                <h2 className="text-xl font-semibold text-red-700 dark:text-red-400">
                  View-as link not valid
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  This view-as link is invalid or has expired (links last ~1 hour).
                  Ask the admin to generate a new one.
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
